import {
  AudienceInstance,
  AudienceType,
  MatchingCriteria,
  PartnerPermissionTypes,
} from '@/client/services/api/graphql/gql/graphql';
import { Avatar, Box, Circle, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { HeavyCheck, UserGroup } from '@/client/components/icons/ContinuIcons';
import { IoGitNetworkOutline, IoLocationOutline } from 'react-icons/io5';

import type { AssignmentFormData } from '../../types/assignmentTypes';
import AssignmentFormPreviewHeader from './AssignmentFormPreviewHeader';
import { GoOrganization } from 'react-icons/go';
import { HiOutlineUserGroup } from 'react-icons/hi2';
import { IconType } from 'react-icons/lib';
import { LiaUserGraduateSolid } from 'react-icons/lia';
import { useAdminAssignmentsStore } from '../../state/adminAssignmentsStore';
import { useEffect } from 'react';
import { useGetAssignentAudienceCount } from '../hooks/useGetAssignentAudienceCount';
import { useTranslation } from 'react-i18next';

// TODO: Since we're no longer using these previews for the overview, the modal preview logic can be removed at some point when we have time.

type AudiencePreviewListProps = {
  audience: AudienceInstance[];
  label: string;
};

function AudiencePreviewList({ audience, label }: AudiencePreviewListProps) {
  const { t } = useTranslation();

  const typeIconMap: { [key: string]: IconType } = {
    location: IoLocationOutline,
    department: IoGitNetworkOutline,
    team: HiOutlineUserGroup,
    org_level: GoOrganization,
    grade: LiaUserGraduateSolid,
    group: HiOutlineUserGroup,
  };

  const audienceList = [
    {
      label: t('admin.assignments.audience.list.users'),
      items: audience.filter((a) => a.segmentationType === 'user'),
    },
    {
      label: t('admin.assignments.audience.list.locations'),
      items: audience.filter((a) => a.segmentationType === 'location'),
    },
    {
      label: t('admin.assignments.audience.list.departments'),
      items: audience.filter((a) => a.segmentationType === 'department'),
    },
    {
      label: t('admin.assignments.audience.list.teams'),
      items: audience.filter((a) => a.segmentationType === 'team'),
    },
    {
      label: t('admin.assignments.audience.list.grades'),
      items: audience.filter((a) => a.segmentationType === 'grade'),
    },
    {
      label: t('admin.assignments.audience.list.orgLevels'),
      items: audience.filter((a) => a.segmentationType === 'org_level'),
    },
    {
      label: t('admin.assignments.audience.list.groups'),
      items: audience.filter((a) => a.segmentationType === 'group'),
    },
  ];

  return (
    <Stack spacing={4}>
      <Text variant="createLabel">{label}</Text>

      {audienceList
        .filter((a) => a.items.length > 0)
        .map((a) => (
          <Stack key={a.label}>
            <Text fontSize="14px" fontWeight={500} color="neutral.800">
              {a.label}
            </Text>

            <HStack flexWrap="wrap" gap={4}>
              {a.items.map((item) => (
                <HStack key={item.id}>
                  {item.type === AudienceType.Segment && (
                    <Circle backgroundColor="warmNeutral.200" size="24px">
                      <Icon color="warmNeutral.1200" as={typeIconMap[item.segmentationType]} />
                    </Circle>
                  )}

                  {item.type === AudienceType.User && (
                    <Avatar
                      src={item.image || undefined}
                      name={item.name}
                      size="xs"
                      backgroundColor="warmNeutral.200"
                      color="warmNeutral.1200"
                    />
                  )}

                  <Text variant="createLabel">{item.name}</Text>
                </HStack>
              ))}
            </HStack>
          </Stack>
        ))}
    </Stack>
  );
}

interface AudienceFormPreviewProps {
  audience: AssignmentFormData['audience'];
  isLocked?: boolean;
  renderStatus?: boolean;
  isModalPreview?: boolean;
  usesBulkAudienceImport?: boolean;
  renderCount?: boolean;
  renderAutomationCountPlaceholder?: boolean;
}

export default function AudienceFormPreview({
  audience,
  isLocked = false,
  renderStatus = false,
  isModalPreview = false,
  usesBulkAudienceImport = false,
  renderCount = false,
  renderAutomationCountPlaceholder = false,
}: AudienceFormPreviewProps) {
  const { t } = useTranslation();
  const { setAudienceCount, audienceFormVisited } = useAdminAssignmentsStore();

  const { data: audienceCountData } = useGetAssignentAudienceCount(audience);

  useEffect(() => {
    if (audienceCountData) {
      setAudienceCount(audienceCountData);
    }
  }, [audienceCountData]);

  const including = audience.filter(
    (a) =>
      (a.matchingCriteria === MatchingCriteria.MatchAll ||
        a.matchingCriteria === MatchingCriteria.MatchAny) &&
      a.type !== AudienceType.HiredDate,
  );

  const includingMatchAll = audience.filter(
    (a) =>
      a.matchingCriteria === MatchingCriteria.MatchAll &&
      a.type !== AudienceType.HiredDate &&
      a.type !== AudienceType.PartnerType,
  );

  const includingMatchAny = audience.filter(
    (a) => a.matchingCriteria === MatchingCriteria.MatchAny && a.type !== AudienceType.HiredDate,
  );

  const excluding = audience.filter(
    (a) => a.matchingCriteria === MatchingCriteria.MatchNone && a.type !== AudienceType.HiredDate,
  );

  const hasHiredDateFilter = audience.some((a) => a.type === AudienceType.HiredDate);

  const hasPartnerTypeFilter = audience.some((a) => a.type === AudienceType.PartnerType);

  const partnerPermissionItems = audience.filter((a) => a.type === AudienceType.PartnerType);

  const hasPartner = partnerPermissionItems.some(
    (item) => item.id === PartnerPermissionTypes.Partner,
  );
  const hasTenant = partnerPermissionItems.some(
    (item) => item.id === PartnerPermissionTypes.Tenant,
  );

  const getPartnerPermissionType = () => {
    if (hasPartner && hasTenant) {
      return PartnerPermissionTypes.PartnerAndTenant;
    }
    if (hasPartner) {
      return PartnerPermissionTypes.Partner;
    }
    if (hasTenant) {
      return PartnerPermissionTypes.Tenant;
    }
    return PartnerPermissionTypes.Partner; // Default to Partner instead of null
  };

  const partnerPermissionTypeMap = {
    [PartnerPermissionTypes.Tenant]: t(
      'admin.assignments.audience.extend.extendAccess.instanceOnly',
    ),
    [PartnerPermissionTypes.Partner]: t(
      'admin.assignments.audience.extend.extendAccess.allExtendAccounts',
    ),
    [PartnerPermissionTypes.PartnerAndTenant]: t(
      'admin.assignments.audience.extend.extendAccess.bothExtendAndInstance',
    ),
  };

  return (
    <Box>
      <AssignmentFormPreviewHeader
        formName="audience"
        icon={UserGroup}
        title={
          isLocked
            ? t('admin.assignments.step.audience.locked')
            : t('admin.assignments.step.audience')
        }
        renderStatus={renderStatus}
        isLocked={isLocked}
      />

      <Stack
        paddingY={isModalPreview ? 0 : 6}
        paddingX={isModalPreview ? 0 : 6}
        spacing={8}
        borderTop="1px solid"
        borderTopColor={audienceFormVisited ? 'neutral.100' : 'transparent'}
      >
        {usesBulkAudienceImport && (
          <Stack spacing={4}>
            <Text variant="createLabel" color="neutral.700">
              {t('admin.assignments.step.audience.totalCount')}
            </Text>

            <Text variant="createLabel">
              {audience.length} {t('admin.assignments.step.audience.totalCount.users')}
            </Text>
          </Stack>
        )}

        {!usesBulkAudienceImport && renderCount && (
          <Stack spacing={4}>
            <Text variant="createLabel" color="neutral.700">
              {t('admin.assignments.step.audience.totalCount')}
            </Text>

            <Text variant="createLabel">{`${audienceCountData ?? 0} users`}</Text>
          </Stack>
        )}

        {renderAutomationCountPlaceholder && audienceFormVisited && (
          <Text variant="createLabel" color="neutral.700">
            {t('admin.assignments.step.audience.totalCount.automation')}
          </Text>
        )}

        {!usesBulkAudienceImport && (including.length > 0 || excluding.length > 0) && (
          <>
            {includingMatchAny.length > 0 && (
              <AudiencePreviewList
                audience={includingMatchAny}
                label={t('admin.assignments.audience.matchAny')}
              />
            )}

            {includingMatchAll.length > 0 && (
              <AudiencePreviewList
                audience={includingMatchAll}
                label={t('admin.assignments.audience.matchAll')}
              />
            )}

            {excluding.length > 0 && (
              <AudiencePreviewList
                audience={excluding}
                label={t('admin.assignments.audience.excluding')}
              />
            )}
          </>
        )}

        {(hasHiredDateFilter || hasPartnerTypeFilter) && (
          <Stack spacing={4}>
            <Text variant="createLabel" color="neutral.700">
              {t('admin.assignments.audienceFilters')}
            </Text>

            {hasPartnerTypeFilter && (
              <HStack>
                <HeavyCheck />

                <Text variant="createLabel">
                  {t('admin.assignments.audience.extend.extendAccess')}{' '}
                  {partnerPermissionTypeMap[getPartnerPermissionType()]}
                </Text>
              </HStack>
            )}

            {hasHiredDateFilter && (
              <HStack>
                <HeavyCheck />

                <Text variant="createLabel">
                  {t('admin.assignments.audience.additionalFilters.hiredWithinDays', {
                    days: audience.find((a) => a.type === AudienceType.HiredDate)?.days || '#',
                  })}
                </Text>
              </HStack>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
